// ============================================================================
//   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
// ----------------------------------------------------------------------------
//   platformGuides (Spec 195 — Universal Logging Onboarding)
//   Registry of platform reference guides surfaced in the Documentation hub.
//   Each guide is rendered in-app (markdown viewer) and downloadable as .md
//   from public/docs/ (synced by scripts/copy-docs.mjs from repo docs/).
// ============================================================================

import { docTopics } from './docTopics';

export interface PlatformGuide {
  /** URL slug for the in-app viewer route (/help/docs/guide/:slug). */
  slug: string;
  /** Human title shown in the hub card + viewer header. */
  title: string;
  /** One-line summary shown on the hub card. */
  description: string;
  /** Markdown filename served from public/docs/. */
  file: string;
  /**
   * Whether the guide offers a "Download .md" action. Platform Guides are meant to
   * be downloaded and used to onboard logging in OTHER codebases, so they default to
   * downloadable. User guides are read in-app only (no download) — set false.
   */
  downloadable?: boolean;
}

/**
 * Platform Guides — downloadable .md references, surfaced in their own "Platform Guides"
 * section. These exist to be pulled into OTHER codebases (e.g. wiring Universal Logging),
 * which is why they carry a Download .md action.
 */
export const platformGuides: PlatformGuide[] = [
  {
    slug: 'universal-logging-onboarding',
    title: 'Universal Logging — Onboarding Guide',
    description:
      'Put a job on the Universal Logging Engine — greenfield + convert paths, self-register (TOFU), fail-open contract.',
    file: 'UNIVERSAL_LOGGING_ONBOARDING.md',
    downloadable: true,
  },
  {
    slug: 'universal-logging-api',
    title: 'Universal Logging — API & Reference',
    description:
      'Endpoints, auth handshake, deployed URLs/audiences, and the error/degradation contract.',
    file: 'UNIVERSAL_LOGGING_API.md',
    downloadable: true,
  },
];

/**
 * User Guides — end-user usage documentation, read in-app only (no download). Surfaced
 * in a "User Guides" section of the Documentation hub so it lives WITH the documentation,
 * not scattered as per-screen help panels.
 */
export const usageGuides: PlatformGuide[] = [
  {
    slug: 'email-notifications',
    title: 'Email Notifications — What You Get & How to Manage It',
    description:
      'The emails Fabric Control sends, how often, who gets them, and how to manage your preferences or stop them.',
    file: 'EMAIL_NOTIFICATIONS.md',
    downloadable: false,
  },
];

/**
 * Every guide the in-app viewer can render (platform guides + user guides + doc topics).
 * Doc topics are the high-level overview docs surfaced as the Documentation hub's topic
 * cards; they render through the same viewer, so findGuide must resolve them too.
 */
const allGuides: PlatformGuide[] = [...platformGuides, ...usageGuides, ...docTopics];

/** Look up a guide by its slug (platform + user guides + doc topics). Undefined if unknown. */
export function findGuide(slug: string | undefined): PlatformGuide | undefined {
  if (!slug) return undefined;
  return allGuides.find((g) => g.slug === slug);
}
